package com.example.borshi.web.nutritionist.ui.components

import androidx.compose.runtime.*
import com.example.borshi.web.nutritionist.ui.theme.*
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLDivElement

/**
 * Modern Button Component with variants and states
 */
enum class ButtonVariant {
    PRIMARY, SECONDARY, GHOST, DANGER
}

enum class ButtonSize {
    SM, MD, LG
}

@Composable
fun ModernButton(
    text: String,
    variant: ButtonVariant = ButtonVariant.PRIMARY,
    size: ButtonSize = ButtonSize.MD,
    disabled: Boolean = false,
    isLoading: Boolean = false,
    fullWidth: Boolean = false,
    icon: (@Composable () -> Unit)? = null,
    onClick: () -> Unit
) {
    Button({
        style {
            // Base styles
            display(DisplayStyle.Flex)
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(Spacing.sm)
            borderRadius(BorderRadius.lg)
            fontWeight(Typography.semibold)
            cursor(if (disabled || isLoading) "not-allowed" else "pointer")
            property("transition", "200ms ease-in-out")
            property("font-family", "inherit")

            if (fullWidth) width(100.percent)

            // Size
            when (size) {
                ButtonSize.SM -> {
                    padding(Spacing.sm, Spacing.md)
                    fontSize(Typography.bodySmall)
                    minHeight(36.px)
                }
                ButtonSize.MD -> {
                    padding(10.px, Spacing.lg)
                    fontSize(Typography.body)
                    minHeight(44.px)
                }
                ButtonSize.LG -> {
                    padding(Spacing.md, Spacing.xl)
                    fontSize(Typography.body)
                    minHeight(48.px)
                }
            }

            // Variant styles
            when (variant) {
                ButtonVariant.PRIMARY -> {
                    backgroundColor(if (disabled || isLoading) ColorTokens.surfaceActive else ColorTokens.primary)
                    color(ColorTokens.textInverse)
                    border(0.px)
                    property("box-shadow", ColorTokens.shadowSm)
                }
                ButtonVariant.SECONDARY -> {
                    backgroundColor(ColorTokens.surface)
                    color(if (disabled) ColorTokens.textTertiary else ColorTokens.textPrimary)
                    border(1.px, LineStyle.Solid, ColorTokens.border)
                }
                ButtonVariant.GHOST -> {
                    backgroundColor(Color("transparent"))
                    color(if (disabled) ColorTokens.textTertiary else ColorTokens.textSecondary)
                    border(0.px)
                }
                ButtonVariant.DANGER -> {
                    backgroundColor(if (disabled || isLoading) ColorTokens.surfaceActive else ColorTokens.danger)
                    color(ColorTokens.textInverse)
                    border(0.px)
                }
            }
        }

        // Hover states (only if not disabled)
        if (!disabled && !isLoading) {
            classes("modern-button-${variant.name.lowercase()}")
        }

        if (disabled || isLoading) {
            attr("disabled", "disabled")
        }

        onClick {
            if (!disabled && !isLoading) {
                onClick()
            }
        }
    }) {
        if (isLoading) {
            LoadingSpinner(size = 16.px)
        } else {
            icon?.invoke()
        }
        Text(text)
    }
}

@Composable
fun LoadingSpinner(size: CSSNumericValue<CSSUnit.px> = 20.px) {
    Div({
        style {
            width(size)
            height(size)
            border(2.px, LineStyle.Solid, Color("transparent"))
            property("border-top-color", "currentColor")
            borderRadius(BorderRadius.full)
            property("animation", "spin 0.6s linear infinite")
        }
    })
}

/**
 * Modern Card Component
 */
@Composable
fun ModernCard(
    hoverable: Boolean = false,
    padding: CSSNumericValue<CSSUnit.px> = Spacing.lg,
    attrs: (AttrsScope<HTMLDivElement>.() -> Unit)? = null,
    content: @Composable () -> Unit
) {
    Div({
        style {
            backgroundColor(ColorTokens.surface)
            borderRadius(BorderRadius.xl)
            padding(padding)
            border(1.px, LineStyle.Solid, ColorTokens.border)
            property("transition", "200ms ease-in-out")

            if (hoverable) {
                property("box-shadow", ColorTokens.shadowSm)
            }
        }

        if (hoverable) {
            classes("hoverable-card")
        }

        attrs?.invoke(this)
    }) {
        content()
    }
}

/**
 * Chip/Badge Component
 */
enum class ChipVariant {
    DEFAULT, SUCCESS, WARNING, DANGER, INFO
}

@Composable
fun Chip(
    label: String,
    variant: ChipVariant = ChipVariant.DEFAULT,
    icon: String? = null,
    small: Boolean = false
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            gap(Spacing.xs)
            padding(if (small) 4.px else 6.px, if (small) Spacing.sm else 12.px)
            borderRadius(BorderRadius.full)
            fontSize(if (small) Typography.tiny else Typography.caption)
            fontWeight(Typography.medium)

            when (variant) {
                ChipVariant.SUCCESS -> {
                    backgroundColor(ColorTokens.successLight)
                    color(ColorTokens.success)
                }
                ChipVariant.WARNING -> {
                    backgroundColor(ColorTokens.warningLight)
                    color(ColorTokens.warning)
                }
                ChipVariant.DANGER -> {
                    backgroundColor(ColorTokens.dangerLight)
                    color(ColorTokens.danger)
                }
                ChipVariant.INFO -> {
                    backgroundColor(ColorTokens.infoLight)
                    color(ColorTokens.info)
                }
                ChipVariant.DEFAULT -> {
                    backgroundColor(ColorTokens.surfaceMuted)
                    color(ColorTokens.textSecondary)
                }
            }
        }
    }) {
        icon?.let {
            Span({ style { fontSize(IconSize.sm) } }) { Text(it) }
        }
        Text(label)
    }
}

/**
 * Avatar Component
 */
@Composable
fun Avatar(
    name: String,
    size: CSSNumericValue<CSSUnit.px> = 48.px,
    imageUrl: String? = null
) {
    Div({
        style {
            width(size)
            height(size)
            borderRadius(BorderRadius.full)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            backgroundColor(ColorTokens.primaryLight)
            color(ColorTokens.primary)
            fontSize(Typography.body)
            fontWeight(Typography.semibold)
            property("flex-shrink", "0")
        }
    }) {
        if (imageUrl != null) {
            Img(imageUrl) {
                style {
                    width(100.percent)
                    height(100.percent)
                    borderRadius(BorderRadius.full)
                    property("object-fit", "cover")
                }
            }
        } else {
            Text(name.take(2).uppercase())
        }
    }
}

/**
 * Skeleton Loader Components
 */
@Composable
fun SkeletonBox(
    width: CSSNumericValue<out CSSUnit> = 100.percent,
    height: CSSNumericValue<CSSUnit.px>,
    borderRadius: CSSNumericValue<CSSUnit.px> = BorderRadius.md
) {
    Div({
        style {
            width(width)
            height(height)
            backgroundColor(ColorTokens.surfaceMuted)
            borderRadius(borderRadius)
            property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
        }
    })
}

@Composable
fun SkeletonCard() {
    ModernCard {
        SkeletonBox(height = 20.px, width = 60.percent)
        Div({ style { height(Spacing.md) } })
        SkeletonBox(height = 16.px, width = 100.percent)
        Div({ style { height(Spacing.sm) } })
        SkeletonBox(height = 16.px, width = 80.percent)
    }
}

/**
 * Icon wrapper (for SVG icons)
 */
@Composable
fun Icon(
    svg: String,
    size: CSSNumericValue<CSSUnit.px> = IconSize.md,
    color: CSSColorValue? = null
) {
    Div({
        style {
            width(size)
            height(size)
            display(DisplayStyle.Flex)
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            color?.let { color(it) }
        }
    }) {
        // For now, use text as placeholder - can be replaced with actual SVG
        Span({ style { fontSize(size) } }) {
            Text(svg)
        }
    }
}

/**
 * Divider Component
 */
@Composable
fun Divider(
    vertical: Boolean = false,
    spacing: CSSNumericValue<CSSUnit.px> = Spacing.lg
) {
    Div({
        style {
            if (vertical) {
                width(1.px)
                height(100.percent)
                property("margin-left", spacing)
                property("margin-right", spacing)
            } else {
                height(1.px)
                width(100.percent)
                property("margin-top", spacing)
                property("margin-bottom", spacing)
            }
            backgroundColor(ColorTokens.border)
        }
    })
}

/**
 * Empty State Component
 */
@Composable
fun EmptyState(
    icon: String,
    title: String,
    message: String,
    action: (@Composable () -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(Spacing.xxxl, Spacing.lg)
            textAlign("center")
            color(ColorTokens.textSecondary)
        }
    }) {
        Div({
            style {
                fontSize(48.px)
                marginBottom(Spacing.md)
                opacity(0.5)
            }
        }) {
            Text(icon)
        }
        H3({
            style {
                fontSize(Typography.subsectionTitle)
                fontWeight(Typography.semibold)
                color(ColorTokens.textPrimary)
                marginBottom(Spacing.sm)
                margin(0.px)
            }
        }) {
            Text(title)
        }
        P({
            style {
                fontSize(Typography.bodySmall)
                margin(0.px)
                marginBottom(Spacing.lg)
                maxWidth(400.px)
            }
        }) {
            Text(message)
        }
        action?.invoke()
    }
}
